/**
 * divtodiv — next experience.
 * Native scroll drives one particle core through the story. No pinning, no scroll-jacking.
 */

import { createCore } from "./core.js";
import { initContactForm } from "../js/contact-form.js";
import { trackPageview } from "../js/analytics.js";

const root = document.documentElement;
const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
const fine = matchMedia("(hover: hover) and (pointer: fine)").matches;
if (fine) root.classList.add("is-fine");
if (reduced) root.classList.add("is-reduced");

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
const lerp = (a, b, t) => a + (b - a) * t;
const ease = (t) => t * t * (3 - 2 * t);

/* ------------------------------------------------------------------ old PWA worker */

// A 2025 version of the site registered a caching service worker. Anyone who
// still has it would keep seeing that cached shell; remove it and its caches.
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations().then((regs) => regs.forEach((r) => r.unregister())).catch(() => {});
}
if ("caches" in window) {
  caches.keys().then((keys) => keys.forEach((k) => caches.delete(k))).catch(() => {});
}

/* ------------------------------------------------------------------ core */

let core = null;
try {
  let fromShape = null;
  try {
    const v = sessionStorage.getItem("core-shape");
    if (v !== null) fromShape = +v;
  } catch {}
  core = createCore(document.getElementById("core"), { reduced, from: fromShape });
} catch (err) {
  console.warn("[next] WebGL core unavailable", err);
}
if (!core) root.classList.add("no-webgl");

const sections = [...document.querySelectorAll("[data-shape]")];
const contact = document.getElementById("contact");

function portrait() {
  return window.innerWidth / window.innerHeight < 0.85;
}

// Forms are ~1.5 units in radius at scale 1; size them to the room they have.
const FORM_R = 1.5;
// The pipeline runs wide (≈ 2.4 units each side); bring it back inside its column.
const SHAPE_SCALE = { 2: 0.7 };
// Upright on phones, it only needs a little trimming
const SHAPE_SCALE_PORTRAIT = { 2: 0.84 };
function stageFor(name) {
  const { halfW, halfH } = core ? core.extent() : { halfW: 4, halfH: 2.2 };
  const fit = (w, h) => Math.min(1.1, Math.min(w, h) / FORM_R);
  if (portrait()) {
    if (name === "hero") return { x: 0, y: halfH * 0.5, s: fit(halfW * 0.72, halfH * 0.3) };
    if (name === "center") return { x: 0, y: 0, s: fit(halfW * 0.9, halfH * 0.6) };
    return { x: 0, y: halfH * 0.42, s: fit(halfW * 0.66, halfH * 0.34) };
  }
  // the sphere starts at ~63% of the viewport; the headline (5.5vw) ends by ~58%
  if (name === "hero") return { x: halfW * 0.6, y: 0.05, s: fit(halfW * 0.34, halfH * 0.78) };
  if (name === "right") return { x: halfW * 0.48, y: 0, s: fit(halfW * 0.38, halfH * 0.66) };
  if (name === "left") return { x: -halfW * 0.5, y: 0, s: fit(halfW * 0.34, halfH * 0.56) };
  return { x: 0, y: 0, s: fit(halfW * 0.7, halfH * 0.7) };
}

/* ------------------------------------------------------------------ scroll state */

let smoothY = window.scrollY;
let lastY = window.scrollY;
let velocity = 0;
let activeChapter = "";
let dawnOn = false;
let lastShape = "";
const themeMeta = document.querySelector('meta[name="theme-color"]');

const progressBar = document.querySelector(".progress__bar");
const header = document.querySelector(".hdr");
const railLinks = [...document.querySelectorAll("[data-rail]")];
const navLinks = [...document.querySelectorAll("[data-chapter-link]")];
const timeline = document.querySelector(".timeline");
const timelineFill = document.querySelector(".timeline__fill");
const timelineItems = timeline ? [...timeline.querySelectorAll("li")] : [];
const fieldSection = document.getElementById("field");
const driftRows = [...document.querySelectorAll("[data-drift]")];

function storyState(y) {
  const vh = window.innerHeight;
  let k = 0;
  for (let i = 0; i < sections.length; i++) {
    if (sections[i].offsetTop - y <= vh * 0.35) k = i;
  }
  const next = Math.min(k + 1, sections.length - 1);
  let mix = 0;
  if (next !== k) {
    const topNext = sections[next].offsetTop - y;
    mix = clamp01((vh - topNext) / (vh * 0.65));
  }
  return { k, next, mix };
}

/* Labels that live beside the 3D forms: SaaS…Data on the stack, Website…Inbox on the flow */
const anchorSets = [...document.querySelectorAll("[data-anchors]")].map((set) => ({
  set,
  kind: set.dataset.anchors,
  labels: [...set.querySelectorAll(".anchor")],
}));

function placeAnchors() {
  anchorSets.forEach(({ set, kind, labels }) => {
    const shape = kind === "stack" ? 1 : 2;
    const show = clamp01((core.presence(shape) - 0.6) / 0.3);
    set.style.opacity = show.toFixed(3);
    if (show <= 0) return;
    labels.forEach((label, i) => {
      const p = kind === "stack" ? core.stackAnchor(i) : core.flowAnchor(i);
      label.style.transform = `translate3d(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px, 0)`;
    });
  });
}

function setChapter(name) {
  if (name === activeChapter) return;
  activeChapter = name;
  railLinks.forEach((a) => a.classList.toggle("is-active", a.dataset.rail === name));
  navLinks.forEach((a) => a.classList.toggle("is-active", a.dataset.chapterLink === name));
}

let lastT = performance.now();
function tick(now) {
  requestAnimationFrame(tick);
  const dt = Math.min((now - lastT) / 1000, 0.05);
  lastT = now;
  const y = window.scrollY;
  const vh = window.innerHeight;

  velocity = lerp(velocity, y - lastY, 0.12);
  lastY = y;
  smoothY = reduced ? y : lerp(smoothY, y, 1 - Math.exp(-9 * dt));

  // the core follows the story
  const { k, next, mix } = storyState(smoothY);
  const a = sections[k];
  const b = sections[next];
  if (core) {
    core.setState({ from: +a.dataset.shape, to: +b.dataset.shape, mix });
    const sa = stageFor(a.dataset.stage);
    const sb = stageFor(b.dataset.stage);
    const scales = portrait() ? SHAPE_SCALE_PORTRAIT : SHAPE_SCALE;
    const ka = scales[a.dataset.shape] ?? 1;
    const kb = scales[b.dataset.shape] ?? 1;
    const e = ease(mix);
    core.setStage({ x: lerp(sa.x, sb.x, e), y: lerp(sa.y, sb.y, e), s: lerp(sa.s * ka, sb.s * kb, e) });
    core.setVelocity(Math.max(-1, Math.min(1, velocity / 45)));
    placeAnchors();
  }
  setChapter((mix > 0.5 ? b : a).dataset.chapter);
  // remembered for the next page, so its core starts from this form
  const dominant = (mix > 0.5 ? b : a).dataset.shape;
  if (dominant !== lastShape) {
    lastShape = dominant;
    try { sessionStorage.setItem("core-shape", dominant); } catch {}
  }

  // dawn: the page turns to light for contact
  if (contact) {
    const top = contact.getBoundingClientRect().top;
    // a short, decisive turn — the half-light in between reads as mud
    const dawn = ease(clamp01((vh * 0.58 - top) / (vh * 0.24)));
    root.style.setProperty("--dawn", dawn.toFixed(3));
    if ((dawn > 0.5) !== dawnOn) {
      dawnOn = dawn > 0.5;
      root.classList.toggle("is-dawn", dawnOn);
      themeMeta?.setAttribute("content", dawnOn ? "#f5f5f7" : "#0a0a0b");
    }
    core?.setDawn(dawn);
  }

  // progress
  const max = document.documentElement.scrollHeight - vh;
  if (progressBar) progressBar.style.transform = `scaleX(${max > 0 ? clamp01(y / max) : 0})`;
  header?.classList.toggle("is-scrolled", y > 40);

  // timeline fills as you read
  if (timeline && timelineFill) {
    const r = timeline.getBoundingClientRect();
    const p = clamp01((vh * 0.62 - r.top) / r.height);
    timelineFill.style.transform = `scaleY(${p})`;
    timelineItems.forEach((li) => {
      li.classList.toggle("is-past", li.getBoundingClientRect().top < vh * 0.62);
    });
  }

  // industries drift with scroll, lean with speed
  if (fieldSection && driftRows.length) {
    const r = fieldSection.getBoundingClientRect();
    if (r.bottom > 0 && r.top < vh) {
      const p = (vh - r.top) / (vh + r.height) - 0.5;
      const skew = Math.max(-8, Math.min(8, velocity * 0.25));
      driftRows.forEach((row) => {
        const dir = +row.dataset.drift;
        row.style.transform = `translate3d(${p * dir * 38}vw,0,0) skewX(${reduced ? 0 : -skew * dir}deg)`;
      });
    }
  }
}
requestAnimationFrame(tick);

/* ------------------------------------------------------------------ pointer */

if (core) {
  window.addEventListener(
    "pointermove",
    (e) => {
      const nx = (e.clientX / window.innerWidth) * 2 - 1;
      const ny = -((e.clientY / window.innerHeight) * 2 - 1);
      core.setPointer(nx, ny, e.pointerType === "mouse" ? 1 : 0.7);
    },
    { passive: true }
  );
  document.addEventListener("pointerleave", () => core.setPointer(0, 0, 0));
  window.addEventListener("pointerup", (e) => {
    if (e.pointerType !== "mouse") core.setPointer(0, 0, 0);
  });
}

/* ------------------------------------------------------------------ hero */

requestAnimationFrame(() => root.classList.add("is-loaded"));

document.querySelectorAll("[data-preview]").forEach((el) => {
  const shape = +el.dataset.preview;
  const on = () => fine && core?.preview(shape);
  const off = () => core?.preview(null);
  el.addEventListener("pointerenter", on);
  el.addEventListener("pointerleave", off);
  el.addEventListener("focus", () => core?.preview(shape));
  el.addEventListener("blur", off);
  el.addEventListener("click", off);
});

/* ------------------------------------------------------------------ phone menu */

const menu = document.getElementById("menu");
const menuButton = document.querySelector(".hdr__menu");

function setMenu(open) {
  if (!menu || !menuButton) return;
  menu.classList.toggle("is-open", open);
  menu.inert = !open;
  menuButton.setAttribute("aria-expanded", String(open));
  menuButton.setAttribute("aria-label", open ? "Close menu" : "Open menu");
  root.classList.toggle("menu-open", open);
}

if (menu && menuButton) {
  menu.inert = true;
  menuButton.addEventListener("click", () => setMenu(!menu.classList.contains("is-open")));
  menu.querySelectorAll("a").forEach((a) => a.addEventListener("click", () => setMenu(false)));
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && menu.classList.contains("is-open")) {
      setMenu(false);
      menuButton.focus();
    }
  });
  // leaving the phone layout closes it
  matchMedia("(min-width: 900px) and (min-aspect-ratio: 17/20)").addEventListener("change", (e) => {
    if (e.matches) setMenu(false);
  });
}

/* ------------------------------------------------------------------ reveals */

function splitWords(el) {
  const parts = [];
  let i = 0;
  [...el.childNodes].forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      node.textContent.split(/(\s+)/).forEach((chunk) => {
        if (!chunk) return;
        if (/^\s+$/.test(chunk)) {
          parts.push(document.createTextNode(" "));
          return;
        }
        const w = document.createElement("span");
        w.className = "w";
        const inner = document.createElement("span");
        inner.textContent = chunk;
        inner.style.setProperty("--i", i++);
        w.appendChild(inner);
        parts.push(w);
      });
    } else {
      parts.push(node.cloneNode(true));
    }
  });
  el.textContent = "";
  parts.forEach((p) => el.appendChild(p));
}
document.querySelectorAll("[data-split]").forEach(splitWords);

const GLYPHS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789/·";
function scramble(el) {
  const final = el.dataset.final || el.textContent;
  el.dataset.final = final;
  if (reduced) return;
  const start = performance.now();
  const dur = 720;
  (function step(now) {
    const t = clamp01((now - start) / dur);
    const settled = Math.floor(final.length * ease(t));
    let out = "";
    for (let i = 0; i < final.length; i++) {
      const c = final[i];
      out += i < settled || c === " " ? c : GLYPHS[(Math.random() * GLYPHS.length) | 0];
    }
    el.textContent = out;
    if (t < 1) requestAnimationFrame(step);
    else el.textContent = final;
  })(start);
}

const revealIO = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      el.classList.add("is-in");
      if (el.hasAttribute("data-scramble")) scramble(el);
      revealIO.unobserve(el);
    });
  },
  { rootMargin: "0px 0px -12% 0px", threshold: 0.12 }
);
document.querySelectorAll("[data-reveal], [data-split], [data-scramble]").forEach((el) => revealIO.observe(el));

/* ------------------------------------------------------------------ 01 stack */

const stackButtons = [...document.querySelectorAll("[data-layer]")];
const stackLabels = [...document.querySelectorAll('[data-anchors="stack"] .anchor')];
function lightLayer(i) {
  core?.setHighlight(i);
  stackButtons.forEach((b) => {
    const on = +b.dataset.layer === i;
    b.classList.toggle("is-on", on);
    b.setAttribute("aria-pressed", String(on));
  });
  stackLabels.forEach((label, k) => label.classList.toggle("is-on", k === i));
}
stackButtons.forEach((b) => {
  const i = +b.dataset.layer;
  b.addEventListener("pointerenter", () => fine && lightLayer(i));
  b.addEventListener("focus", () => lightLayer(i));
  b.addEventListener("click", () => lightLayer(i));
});
document.querySelector(".stack")?.addEventListener("pointerleave", () => fine && lightLayer(null));

if (!fine) {
  // touch: the row crossing the middle of the screen lights its layer
  const rowIO = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) lightLayer(+entry.target.dataset.layer);
      });
    },
    { rootMargin: "-46% 0px -46% 0px" }
  );
  stackButtons.forEach((b) => rowIO.observe(b));
}

/* ------------------------------------------------------------------ 02 pipeline */

function whenVisible(el, start, stop) {
  if (!el) return;
  new IntersectionObserver((entries) => {
    entries.forEach((entry) => (entry.isIntersecting ? start() : stop()));
  }, { threshold: 0.25 }).observe(el);
}

(() => {
  const pipe = document.querySelector(".pipeline");
  if (!pipe) return;
  const steps = [...pipe.querySelectorAll(".step")];
  const clock = pipe.querySelector(".pipeline__clock");
  const flowLabels = [...document.querySelectorAll('[data-anchors="flow"] .anchor')];
  const last = steps.length - 1;
  let timer = 0;
  let headRaf = 0;
  let seconds = 7 * 3600 + 58 * 60 + 12;
  let running = false;

  // the light in the 3D pipeline travels to the system that is working
  function travel(i) {
    cancelAnimationFrame(headRaf);
    const from = Math.max(0, (i - 1) / last);
    const to = i / last;
    const start = performance.now();
    const dur = i === 0 ? 400 : 900;
    (function step(now) {
      const t = ease(clamp01((now - start) / dur));
      core?.setFlow({ head: lerp(from, to, t), node: t >= 1 ? i : i - 1 });
      if (t < 1) headRaf = requestAnimationFrame(step);
    })(start);
    flowLabels.forEach((label, k) => {
      label.classList.toggle("is-on", k === i);
      label.classList.toggle("is-done", k < i);
    });
  }

  function quiet() {
    cancelAnimationFrame(headRaf);
    core?.setFlow({ head: -1, node: -1 });
    flowLabels.forEach((label) => label.classList.remove("is-on", "is-done"));
  }

  const fmt = (s) =>
    [Math.floor(s / 3600), Math.floor((s % 3600) / 60), s % 60].map((n) => String(n).padStart(2, "0")).join(":");

  function setState(i, state) {
    const step = steps[i];
    step.classList.remove("is-run", "is-done");
    if (state) step.classList.add(state);
    step.querySelector(".step__state").textContent = state === "is-done" ? "Done" : state === "is-run" ? "Running" : "Queued";
  }

  function reset() {
    steps.forEach((_, i) => setState(i, null));
  }

  function run(i = 0) {
    if (!running) return;
    if (i === 0) {
      reset();
      quiet();
    }
    if (i >= steps.length) {
      timer = setTimeout(() => run(0), 2200);
      return;
    }
    setState(i, "is-run");
    travel(i);
    timer = setTimeout(() => {
      setState(i, "is-done");
      seconds += 2 + Math.floor(Math.random() * 4);
      clock.textContent = fmt(seconds);
      timer = setTimeout(() => run(i + 1), 220);
    }, 950);
  }

  if (reduced) {
    steps.forEach((_, i) => setState(i, "is-done"));
    return;
  }
  reset();
  whenVisible(
    pipe,
    () => {
      if (running) return;
      running = true;
      run(0);
    },
    () => {
      running = false;
      clearTimeout(timer);
      quiet();
    }
  );
})();

/* ------------------------------------------------------------------ 03 console */

(() => {
  const box = document.querySelector(".console");
  if (!box) return;
  const promptEl = box.querySelector("[data-type-prompt]");
  const outEl = box.querySelector("[data-type-out]");
  const PROMPT = "Summarize this week's bookings and flag anything unusual.";
  const OUT = [
    "Bookings this week: 142, up 9% on last week.",
    "Flagged: 3 cancellations from one account on Tuesday.",
    "Suggested: hold that account for review before the next invoice run.",
  ];
  let timer = 0;
  let running = false;

  function caret(el) {
    box.querySelectorAll(".console__cursor").forEach((c) => c.remove());
    const c = document.createElement("span");
    c.className = "console__cursor";
    el.appendChild(c);
  }

  function typePrompt(i = 0) {
    if (!running) return;
    if (i === 0) core?.setThink(0.3);
    promptEl.textContent = PROMPT.slice(0, i);
    caret(promptEl);
    if (i < PROMPT.length) {
      timer = setTimeout(() => typePrompt(i + 1), 22 + Math.random() * 30);
    } else {
      timer = setTimeout(() => stream(0, 0), 520);
    }
  }

  function stream(line, word) {
    if (!running) return;
    // the brain beside it works while the answer streams
    core?.setThink(1);
    const words = OUT.map((l) => l.split(" "));
    outEl.innerHTML = "";
    for (let l = 0; l <= line && l < OUT.length; l++) {
      const p = document.createElement("span");
      p.className = "console__line";
      p.textContent = l < line ? OUT[l] : words[l].slice(0, word).join(" ");
      outEl.appendChild(p);
    }
    caret(outEl.lastChild || outEl);
    if (line >= OUT.length) return;
    if (word < words[line].length) {
      timer = setTimeout(() => stream(line, word + 1), 55 + Math.random() * 45);
    } else if (line + 1 < OUT.length) {
      timer = setTimeout(() => stream(line + 1, 0), 260);
    } else {
      core?.setThink(0.12);
      timer = setTimeout(() => {
        outEl.textContent = "";
        typePrompt(0);
      }, 5200);
    }
  }

  if (reduced) {
    promptEl.textContent = PROMPT;
    outEl.innerHTML = OUT.map((l) => `<span class="console__line">${l}</span>`).join("");
    return;
  }
  whenVisible(
    box,
    () => {
      if (running) return;
      running = true;
      outEl.textContent = "";
      typePrompt(0);
    },
    () => {
      running = false;
      clearTimeout(timer);
      core?.setThink(0);
    }
  );
})();

/* ------------------------------------------------------------------ 04 cloud / on-prem */

(() => {
  const sw = document.querySelector(".switch");
  if (!sw) return;
  const buttons = [...sw.querySelectorAll("[data-mode]")];
  const values = [...document.querySelectorAll(".compare dd")];
  function setMode(mode) {
    sw.dataset.mode = mode;
    buttons.forEach((b) => b.setAttribute("aria-pressed", String(b.dataset.mode === mode)));
    values.forEach((dd, i) => {
      dd.classList.add("is-swap");
      setTimeout(() => {
        dd.textContent = mode === "onprem" ? dd.dataset.onprem : dd.dataset.cloud;
        dd.classList.remove("is-swap");
      }, reduced ? 0 : 160 + i * 40);
    });
    core?.setContain(mode === "onprem" ? 1 : 0);
  }
  buttons.forEach((b) => b.addEventListener("click", () => setMode(b.dataset.mode)));
  sw.dataset.mode = "cloud";
})();

/* ------------------------------------------------------------------ cursor + magnetic */

if (fine && !reduced) {
  const cursor = document.querySelector(".cursor");
  let cx = -100, cy = -100, tx = -100, ty = -100;
  window.addEventListener("pointermove", (e) => {
    tx = e.clientX;
    ty = e.clientY;
    if (!cursor.classList.contains("is-live")) {
      // appear where the pointer is, not fly in from the corner
      cx = tx;
      cy = ty;
      cursor.classList.add("is-live");
    }
    const hot = e.target.closest?.("a, button, summary, [data-preview], input, textarea");
    cursor.classList.toggle("is-hot", !!hot);
    cursor.classList.toggle("is-text", !!e.target.closest?.("input, textarea"));
  }, { passive: true });
  (function follow() {
    cx = lerp(cx, tx, 0.2);
    cy = lerp(cy, ty, 0.2);
    cursor.style.transform = `translate3d(${cx}px, ${cy}px, 0)`;
    requestAnimationFrame(follow);
  })();
  root.classList.add("has-cursor");

  document.querySelectorAll("[data-magnetic]").forEach((el) => {
    el.addEventListener("pointermove", (e) => {
      const r = el.getBoundingClientRect();
      const dx = e.clientX - (r.left + r.width / 2);
      const dy = e.clientY - (r.top + r.height / 2);
      el.style.transform = `translate(${dx * 0.22}px, ${dy * 0.32}px)`;
    });
    el.addEventListener("pointerleave", () => {
      el.style.transform = "";
    });
  });
}

/* ------------------------------------------------------------------ contact */

initContactForm();
trackPageview();
